<template>
  <div class="event-form py-1">
    <v-row>
      <v-col cols="12" md="6">
        <p class="text-subtitle-1">
          <strong class="pr-2 font-weight-normal">
            {{ $t('EMPLOYEES.ITEM') }}:
          </strong>
          <span class="font-weight-normal text--secondary">
            {{ employeeTitle }}
          </span>
          <address-line
            v-if="form.employee"
            class="d-block font-weight-normal text--secondary"
            v-bind="form.employee"
          />
        </p>
      </v-col>
      <v-col cols="12" md="6">
        <p class="text-subtitle-1">
          <strong class="pr-2">{{ $t('ITEM.DATE') }}:</strong>
          <span class="font-weight-normal text--secondary">
            {{ form.date_from | dateFormat }}
          </span>
        </p>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" md="6">
        <v-row align="center">
          <v-col cols="12">
            <v-menu
              ref="timeFrom"
              v-model="timeFrom"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.time_from"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.time_from"
                  name="time_from"
                  :label="`${$t('PLANNINGS.TIME_FROM')} *`"
                  prepend-icon="mdi-clock-time-four-outline"
                  :clearable="!readonly"
                  readonly
                  :rules="getRulesByName('time_from')"
                  :error-messages="errors.time_from"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="timeFrom"
                v-model="form.time_from"
                :allowed-minutes="allowedStep"
                :max="form.time_to"
                :readonly="readonly"
                format="24hr"
                scrollable
                @click:minute="$refs.timeFrom.save(form.time_from)"
                @input="updateValue('time_from', $event)"
              ></v-time-picker>
            </v-menu>
          </v-col>
          <v-col cols="12">
            <v-menu
              ref="timeTo"
              v-model="timeTo"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.time_to"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.time_to"
                  name="time_to"
                  prepend-icon="mdi-clock-time-four-outline"
                  :label="`${$t('PLANNINGS.TIME_TO')} *`"
                  :rules="getRulesByName('time_to')"
                  :error-messages="errors.time_to"
                  :clearable="!readonly"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="timeTo"
                v-model="form.time_to"
                :allowed-minutes="allowedStep"
                format="24hr"
                :min="form.time_from"
                :readonly="readonly"
                scrollable
                @click:minute="$refs.timeTo.save(form.time_to)"
                @input="updateValue('time_to', $event)"
              ></v-time-picker>
            </v-menu>
          </v-col>
          <v-col cols="12">
            <p class="text-subtitle-1 text--secondary">
              <strong class="pr-2">{{ $t('ITEM.DURATION') }}:</strong>
              <span>{{ duration }}</span>
            </p>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" md="6">
        <v-textarea
          v-model="form.info"
          name="info"
          :rows="6"
          :label="$t('ITEM.ADDITIONAL_INFO')"
          :readonly="readonly"
          :rules="getRulesByName('info')"
          :error-messages="errors.info"
          outlined
          @input="updateValue('info', $event)"
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <v-sheet class="px-3 py-1" tile outlined>
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="is_multi_task"
                name="is_multi_task"
                :label="$t('PLANNINGS.MULTI_TASKS')"
                :readonly="readonlyMultiTaskForm"
                :error="errors.is_multi_task"
                :disabled="disableMultiTaskSwitch"
                @change="updateValue('is_multi_task', $event)"
              ></v-switch>
            </v-col>
            <v-col v-if="form.is_multi_task" cols="12">
              <event-multi-tasks-form
                v-model="form"
                :readonly="readonlyMultiTaskForm"
                :disable-delete="disableDelete"
                :errors="errors"
                @input="emitter"
                @remove="$emit('remove')"
                @update-prop="updateProp(...arguments)"
              ></event-multi-tasks-form>
            </v-col>
          </v-row>
        </v-sheet>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import duration from '@/modules/_global/mixins/duration-time';
import formEditable from '@/modules/_global/mixins/form-editable';
import eventFrom from './mixins/event-form';
import { notEmpty } from '@/modules/_global/variables/rules';
import { dateFormat } from '@/modules/_global/filters';
import AddressLine from '@/modules/_global/_components/AddressLine';

export default {
  name: 'EventForm',

  filters: {
    dateFormat
  },

  components: {
    AddressLine,
    EventMultiTasksForm: () => import('./EventMultiTasksForm')
  },

  mixins: [eventFrom, formEditable, duration],

  props: {
    order: {
      type: Object,
      required: true
    },
    disableMultiTask: {
      type: Boolean,
      default: false
    },
    disableDelete: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      timeFrom: false,
      timeTo: false,
      form: {
        info: '',
        time_from: null,
        time_to: null,
        is_multi_task: false
      },
      rules: {
        time_from: [notEmpty],
        time_to: [notEmpty]
      }
    };
  },

  computed: {
    is_multi_task: {
      get() {
        return Boolean(this.form.is_multi_task);
      },
      set(newVal) {
        this.form.is_multi_task = newVal;
      }
    },

    duration() {
      return this.getDuration(this.form.time_from, this.form.time_to);
    },

    readonlyMultiTaskForm() {
      return this.readonly || Boolean(this.form.id);
    },

    employeeTitle() {
      const { employee } = this.form;

      if (!employee) {
        return '';
      }

      const first = employee.first_name || '';
      const last = employee.last_name || '';

      return `${first} ${last}`.trim();
    },

    disableMultiTaskSwitch() {
      return this.disableMultiTask || Boolean(this.order.is_tender);
    }
  }
};
</script>

<style lang="scss">
.event-form {
  .row {
    margin-top: -4px;
    margin-bottom: -4px;

    & + .row {
      margin-top: 4px;
    }

    > .col,
    > [class*='col-'] {
      padding-top: 4px;
      padding-bottom: 4px;
    }

    &.no-gutters > .col,
    &.no-gutters > [class*='col-'] {
      padding: 0;
    }
  }
}
</style>
